import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { requestToJoin, requestToLeave, checkValidatorEpochStatus } from '../../services/staking';
import { useRecoilState } from 'recoil';
import { contractAddressesState } from '../../context/appContext';
import { useEffect, useState, useRef, useCallback } from 'react';
import { getSigner } from '../../lib/web3';

function ActivateNodeButtons() {
  const [stakingContractAddress] = useRecoilState(contractAddressesState);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkNodeState = useCallback(async () => {
    if (!stakingContractAddress?.stakingAddress) {
      setIsDisabled(false);
      return;
    }

    try {
      setIsLoading(true);
      const signer = await getSigner();
      const addr = await signer.getAddress();

      // Check epoch status
      const epochStatus = await checkValidatorEpochStatus(
        stakingContractAddress.stakingAddress,
        addr
      );

      // Disable if validator is in current or next epoch (not inactive)
      // Node state is inactive only when NOT in current AND NOT in next epoch
      const isInactive = !epochStatus.inCurrentEpoch && !epochStatus.inNextEpoch;
      setIsDisabled(!isInactive);
    } catch (e: any) {
      console.error('Error checking node state:', e);
      // If wallet not connected or error, allow activation attempt
      setIsDisabled(false);
    } finally {
      setIsLoading(false);
    }
  }, [stakingContractAddress?.stakingAddress]);

  useEffect(() => {
    // Initial check
    checkNodeState();

    // Set up interval to check every 10 seconds
    intervalRef.current = setInterval(() => {
      checkNodeState();
    }, 10000);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkNodeState]);

  const handleJoin = async () => {
    try {
      await requestToJoin(stakingContractAddress.stakingAddress);
    } catch (e: any) {
      console.log('Error requesting to join: ', e);
      alert('Error requesting to join: ' + e.message);
    }
  };

  const handleLeave = async () => {
    try {
      await requestToLeave(stakingContractAddress.stakingAddress);
    } catch (e: any) {
      console.log('Error requesting to leave: ', e);
      alert('Error requesting to leave: ' + e.message);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Button
            onClick={handleJoin}
            variant={'contained'}
            disabled={isDisabled || isLoading}
          >
            Activate Node
          </Button>
          <Button
            onClick={handleLeave}
            variant={'contained'}
            disabled={!isDisabled || isLoading}
          >
            Request To Leave
          </Button>
          {isDisabled && (
            <Typography variant={'body2'} color={'error'}>
              Node must be inactive to activate. Current node state is not inactive.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ActivateNodeButtons;
